import { useSyncExternalStore } from 'react';

/**
 * App-wide save-status store: who has uncommitted text, how many writes are in flight, and whether the
 * last save failed. Deliberately a plain module-level store (the `hover-editor-engagement` precedent)
 * rather than Redux:
 *
 *  - `beforeunload` needs a SYNCHRONOUS read at the moment the tab is closing (`isAnyUnsaved`).
 *  - the `ui` slice is redux-persisted, and a persisted "saving"/"dirty" flag would resurrect a stale
 *    state after a reload — exactly the lie this whole mechanism exists to prevent.
 *
 * `lastSaved` stays in Redux (`ui.lastSaved`), unchanged. Only the transient signals live here.
 */

/** An editor holding text that is not yet handed to the save path. */
interface UnsavedEditor {
  /** Human label for diagnostics (e.g. the field title) — surfaced in the indicator tooltip. */
  label?: string;
  /** Commit the outstanding draft. Called on unmount and on unload; must be safe to call twice. */
  flush: () => void;
}

/** A save that failed or was blocked. Kept until the user retries or explicitly discards. */
export interface SaveErrorRecord {
  /** User-facing sentence. Must make clear the text is NOT saved. */
  message: string;
  /** Re-attempt the save. Implementations MUST rebase onto current server state, never replay blindly. */
  retry: () => void;
  /**
   * Abandon the unsaved text and adopt server state. Performs the discard IMMEDIATELY — confirmation is
   * the caller's job, so the UI can confirm once for several failures instead of once per record.
   */
  discard: () => void;
}

export interface SaveStatusSnapshot {
  /** Number of editors currently holding uncommitted text. */
  dirtyCount: number;
  /** Labels of those editors (for the indicator tooltip). */
  dirtyLabels: string[];
  /** Number of Firestore writes awaiting server acknowledgement. */
  writesInFlight: number;
  /**
   * Every unresolved save failure. A list, not a single slot: two editors can each fail (or a flex and
   * a static write can fail together), and a single slot would make the earlier failure's retry
   * unreachable — silently stranding that draft, which is the bug class this store exists to prevent.
   */
  errors: SaveErrorRecord[];
}

const editors = new Map<object, UnsavedEditor>();
let writesInFlight = 0;
let errors: SaveErrorRecord[] = [];

const listeners = new Set<() => void>();

// useSyncExternalStore requires a referentially stable snapshot between changes, or it re-renders
// forever. Rebuild the cached object only when something actually changed.
let snapshot: SaveStatusSnapshot = { dirtyCount: 0, dirtyLabels: [], writesInFlight: 0, errors: [] };

const emit = () => {
  snapshot = {
    dirtyCount: editors.size,
    dirtyLabels: Array.from(editors.values(), (e) => e.label || 'Unnamed field'),
    writesInFlight,
    errors
  };
  listeners.forEach((l) => l());
};

/**
 * Declare that `id` holds uncommitted text. Idempotent — re-registering only refreshes the flush
 * closure (which every keystroke does, so the flush always sees the latest draft).
 */
export function registerUnsaved(id: object, editor: UnsavedEditor) {
  const existing = editors.get(id);
  editors.set(id, editor);
  // Only notify on a genuine membership change; refreshing the closure must not re-render the app on
  // every keystroke.
  if (!existing) emit();
}

/** Declare `id` clean — its text has been handed to the save path, or deliberately discarded. */
export function clearUnsaved(id: object) {
  if (editors.delete(id)) emit();
}

/**
 * Commit every outstanding draft. Used by the unload guard and the two `window.location.reload()`
 * paths. Each flush hands its text to the save path, which then clears the entry, so iterate over a
 * copy. Flushes run synchronously and in registration order; `commit()` in the save layer updates its
 * ref synchronously before its async write, so successive flushes chain rather than clobber.
 */
export function flushAllDirty() {
  Array.from(editors.entries()).forEach(([id, editor]) => {
    try {
      editor.flush();
    } catch {
      /* one bad editor must never block the rest */
    }
    editors.delete(id);
  });
  emit();
}

/** Mark a Firestore write as started (paired with `endWrite` in a `finally`). */
export function beginWrite() {
  writesInFlight += 1;
  emit();
}

/** Mark a Firestore write as settled — acknowledged, failed, or blocked. */
export function endWrite() {
  writesInFlight = Math.max(0, writesInFlight - 1);
  emit();
}

/** Add an unresolved save failure. Each failure keeps its own retry, so none is ever stranded. */
export function addSaveError(record: SaveErrorRecord) {
  if (errors.includes(record)) return;
  errors = [...errors, record];
  emit();
}

/** Resolve one failure (retried successfully, or discarded). No-op if it is already gone. */
export function removeSaveError(record: SaveErrorRecord) {
  if (!errors.includes(record)) return;
  errors = errors.filter((e) => e !== record);
  emit();
}

export function getSaveErrors(): SaveErrorRecord[] {
  return errors;
}

/**
 * Synchronous "is anything at risk right now?" — uncommitted text, an unacknowledged write, or an
 * unresolved failure. This is what the `beforeunload` guard reads; it cannot await anything.
 */
export function isAnyUnsaved(): boolean {
  return editors.size > 0 || writesInFlight > 0 || errors.length > 0;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => snapshot;

/** Reactive read of the save status (the header indicator). */
export function useSaveStatus(): SaveStatusSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
